import { resolveCombat } from './combat.js';
import { applyForfeit } from './forfeit.js';
import { createPrng, type Prng } from './rng.js';
import { computeReinforcements, countOwned } from './state.js';
import {
  type Action,
  type ApplyResult,
  type GameEvent,
  type GameState,
  type LogEntry,
  type MapDefinition,
  type Player,
  type PlayerId,
  type TerritoryId,
  type TerritoryState,
  type TurnContext,
  type TurnPhase,
} from './types.js';
import { validateAction } from './validation.js';

/** Cap retained battle-log entries by attack count. */
const MAX_ATTACKS_IN_HISTORY = 100;

/**
 * Reducer. Returns a new state and an event log derived from the action.
 *
 * Re-validates the action defensively so direct callers get a loud error
 * rather than silent state corruption — the cost is negligible and stores
 * already validate before dispatch.
 *
 * `prng` is optional. If omitted, the reducer creates one from
 * `state.rngSeed` and stores `prng.getSeed()` back onto the new state. Tests
 * pass a scriptedPrng to force deterministic combat without seed-hunting.
 */
export function applyAction(
  state: GameState,
  action: Action,
  map: MapDefinition,
  prng?: Prng
): ApplyResult {
  const v = validateAction(state, action, map);
  if (!v.ok) {
    throw new Error(`applyAction: invalid action — ${v.reason}`);
  }

  let inner: ApplyResult;
  switch (action.type) {
    case 'deploy':
      inner = applyDeploy(state, action);
      break;
    case 'attack':
      inner = applyAttack(state, action, prng);
      break;
    case 'advance':
      inner = applyAdvance(state, action);
      break;
    case 'fortify':
      inner = applyFortify(state, action);
      break;
    case 'endPhase':
      inner = applyEndPhase(state, map);
      break;
    case 'endTurn':
      inner = applyEndTurn(state, map);
      break;
    case 'undoLastDeploy':
      inner = applyUndoLastDeploy(state);
      break;
    case 'autoSkip':
      inner = applyAutoSkip(state, action, map);
      break;
  }

  // v0.9.9 Hourglass — dispatcher-level counter reset. Any non-skip
  // action by a player resets their consecutiveAutoSkips counter to
  // 0. This is cross-cutting (same rule for every action type), so
  // it lives here next to the history-append wrap rather than being
  // duplicated across each per-action reducer.
  const innerState =
    action.type === 'autoSkip'
      ? inner.state
      : resetSkipCounter(inner.state, action.playerId);

  // Append the action + its events to the battle-log history. Use the
  // pre-reduction state's turnNumber so the entry is tagged with the
  // turn it RAN ON, not the turn it transitions TO. (Important for
  // 'endTurn', which bumps turnNumber as part of its work.)
  const lastId =
    state.history.length === 0
      ? -1
      : state.history[state.history.length - 1]!.id;
  const entry: LogEntry = {
    id: lastId + 1,
    turnNumber: state.turnNumber,
    actor: action.playerId,
    action,
    events: inner.events,
  };
  const history = trimHistoryToLast100Attacks([...state.history, entry]);
  return {
    state: { ...innerState, history },
    events: inner.events,
  };
}

/** Drop the player's consecutive-skip counter entry (effectively
 *  resets to 0 since missing keys read as 0). Pure — never mutates
 *  the input map. */
function resetSkipCounter(
  state: GameState,
  playerId: PlayerId
): GameState {
  const current = state.consecutiveAutoSkips ?? {};
  if (!(playerId in current)) return state; // already 0, nothing to do
  const { [playerId]: _, ...rest } = current;
  return { ...state, consecutiveAutoSkips: rest };
}

/**
 * Walk the history newest → oldest, count entries whose action is an
 * attack. Once we cross MAX_ATTACKS_IN_HISTORY, drop everything older
 * than the boundary entry. Non-attack entries (deploys, fortifies,
 * etc.) interleaved among the kept attacks are preserved; only
 * entries STRICTLY OLDER than the cap-th most recent attack are
 * trimmed. Pure function.
 */
function trimHistoryToLast100Attacks(
  history: readonly LogEntry[]
): readonly LogEntry[] {
  let attacks = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]!.action.type === 'attack') {
      attacks++;
      if (attacks > MAX_ATTACKS_IN_HISTORY) {
        return history.slice(i + 1);
      }
    }
  }
  return history;
}

// --- Per-action reducers ---

function applyDeploy(
  state: GameState,
  action: Extract<Action, { type: 'deploy' }>
): ApplyResult {
  const old = state.territories[action.territory]!;
  const territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
    [action.territory]: { ...old, units: old.units + action.units },
  };
  const turn: TurnContext = {
    ...state.turn,
    reinforcementsRemaining:
      state.turn.reinforcementsRemaining - action.units,
    // Append a record so the player can undo this deploy until they
    // end the deploy phase.
    deploysThisTurn: [
      ...state.turn.deploysThisTurn,
      { territory: action.territory, units: action.units },
    ],
  };
  return {
    state: { ...state, territories, turn },
    events: [
      {
        kind: 'deployed',
        playerId: action.playerId,
        territory: action.territory,
        units: action.units,
      },
    ],
  };
}

function applyUndoLastDeploy(state: GameState): ApplyResult {
  const stack = state.turn.deploysThisTurn;
  // Validation already guaranteed non-empty stack, but be defensive.
  if (stack.length === 0) {
    return { state, events: [] };
  }
  const last = stack[stack.length - 1]!;
  const tile = state.territories[last.territory]!;
  const territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
    [last.territory]: { ...tile, units: tile.units - last.units },
  };
  const turn: TurnContext = {
    ...state.turn,
    reinforcementsRemaining:
      state.turn.reinforcementsRemaining + last.units,
    deploysThisTurn: stack.slice(0, -1),
  };
  return {
    state: { ...state, territories, turn },
    events: [
      {
        kind: 'deployUndone',
        territory: last.territory,
        units: last.units,
      },
    ],
  };
}

function applyAttack(
  state: GameState,
  action: Extract<Action, { type: 'attack' }>,
  injectedPrng: Prng | undefined
): ApplyResult {
  const from = state.territories[action.from]!;
  const to = state.territories[action.to]!;
  const prng = injectedPrng ?? createPrng(state.rngSeed);

  const result = resolveCombat(
    {
      from: action.from,
      to: action.to,
      attackerUnits: from.units,
      defenderUnits: to.units,
      attackingDice: action.attackingDice,
    },
    prng
  );

  const events: GameEvent[] = [{ kind: 'combatResolved', result }];

  const newFromUnits = from.units - result.attackerLosses;
  const newToUnits = to.units - result.defenderLosses;

  let territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
    [action.from]: { ...from, units: newFromUnits },
    [action.to]: { ...to, units: newToUnits },
  };

  let turn: TurnContext = { ...state.turn, lastCombat: result };

  if (result.conquered) {
    // Ownership transfers immediately; advance fills units in.
    territories = {
      ...territories,
      [action.to]: { owner: action.playerId, units: 0 },
    };
    events.push({
      kind: 'territoryCaptured',
      territory: action.to,
      from: to.owner,
      to: action.playerId,
    });
    const maxUnits = newFromUnits - 1;
    const minUnits = Math.min(action.attackingDice, maxUnits);
    turn = {
      ...turn,
      attack: {
        kind: 'awaitingAdvance',
        from: action.from,
        to: action.to,
        minUnits,
        maxUnits,
      },
    };
  }

  let next: GameState = {
    ...state,
    territories,
    turn,
    rngSeed: prng.getSeed(),
  };

  const elim = checkEliminationsAndVictory(next);
  next = elim.state;
  events.push(...elim.events);

  return { state: next, events };
}

function applyAdvance(
  state: GameState,
  action: Extract<Action, { type: 'advance' }>
): ApplyResult {
  const from = state.territories[action.from]!;
  const to = state.territories[action.to]!;
  const territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
    [action.from]: { ...from, units: from.units - action.units },
    [action.to]: { ...to, units: to.units + action.units },
  };
  const turn: TurnContext = {
    ...state.turn,
    attack: { kind: 'free' },
  };
  return {
    state: { ...state, territories, turn },
    events: [
      {
        kind: 'advanced',
        from: action.from,
        to: action.to,
        units: action.units,
      },
    ],
  };
}

function applyFortify(
  state: GameState,
  action: Extract<Action, { type: 'fortify' }>
): ApplyResult {
  const from = state.territories[action.from]!;
  const to = state.territories[action.to]!;
  const territories: Record<TerritoryId, TerritoryState> = {
    ...state.territories,
    [action.from]: { ...from, units: from.units - action.units },
    [action.to]: { ...to, units: to.units + action.units },
  };
  const turn: TurnContext = { ...state.turn, fortifyUsed: true };
  return {
    state: { ...state, territories, turn },
    events: [
      {
        kind: 'fortified',
        from: action.from,
        to: action.to,
        units: action.units,
      },
    ],
  };
}

function applyEndPhase(state: GameState, map: MapDefinition): ApplyResult {
  // Setup round (turn 0): ending deploy hands the seat to the next player's
  // deploy without entering attack/fortify. When the rotation wraps back to
  // playerOrder[0], turnNumber bumps to 1 and the normal cycle resumes.
  if (state.turnNumber === 0 && state.turn.phase === 'deploy') {
    return applySetupHandoff(state, map);
  }

  let nextPhase: TurnPhase;
  switch (state.turn.phase) {
    case 'deploy':
      nextPhase = 'attack';
      break;
    case 'attack':
      nextPhase = 'fortify';
      break;
    case 'fortify':
      throw new Error('endPhase from fortify is invalid (use endTurn)');
  }
  // Construct a fresh TurnContext so transient fields (lastCombat, attack
  // sub-state, deploysThisTurn) don't leak into the next phase.
  const turn: TurnContext = {
    currentPlayer: state.turn.currentPlayer,
    phase: nextPhase,
    attack: { kind: 'free' },
    reinforcementsRemaining: state.turn.reinforcementsRemaining,
    fortifyUsed: state.turn.fortifyUsed,
    deploysThisTurn: [],
  };
  return {
    state: { ...state, turn },
    events: [{ kind: 'phaseChanged', phase: nextPhase }],
  };
}

function applySetupHandoff(
  state: GameState,
  map: MapDefinition
): ApplyResult {
  const next = findNextPlayer(state, state.turn.currentPlayer);
  const turnNumber = state.turnNumber + next.turnIncrement;
  const wrapped = next.turnIncrement === 1;

  // The setup deploys count as every seat's first round of reinforcements.
  // When the rotation wraps, turn 1 starts in attack phase — no second
  // deploy this round. Turn 2+ resumes the normal deploy → attack → fortify
  // cycle.
  const phase: TurnPhase = wrapped ? 'attack' : 'deploy';
  const reinforcements = wrapped ? 0 : computeReinforcements(state, map, next.id);

  const turn: TurnContext = {
    currentPlayer: next.id,
    phase,
    attack: { kind: 'free' },
    reinforcementsRemaining: reinforcements,
    fortifyUsed: false,
    deploysThisTurn: [],
  };
  return {
    state: { ...state, turn, turnNumber },
    events: [{ kind: 'turnEnded', nextPlayer: next.id, turnNumber }],
  };
}

function applyEndTurn(state: GameState, map: MapDefinition): ApplyResult {
  const next = findNextPlayer(state, state.turn.currentPlayer);
  const turnNumber = state.turnNumber + next.turnIncrement;

  // Turn 1 is the combat-only round (setup already counted as everyone's
  // first deploy). From turn 2 onwards each turn starts with a fresh
  // deploy phase as in standard Risk.
  const phase: TurnPhase = turnNumber === 1 ? 'attack' : 'deploy';
  const reinforcements =
    phase === 'deploy' ? computeReinforcements(state, map, next.id) : 0;

  const turn: TurnContext = {
    currentPlayer: next.id,
    phase,
    attack: { kind: 'free' },
    reinforcementsRemaining: reinforcements,
    fortifyUsed: false,
    deploysThisTurn: [],
  };
  return {
    state: { ...state, turn, turnNumber },
    events: [
      { kind: 'turnEnded', nextPlayer: next.id, turnNumber },
    ],
  };
}

/**
 * v0.9.9 Hourglass — auto-skip the current player's entire turn.
 * Server-only (cron-driven, never client-dispatched). Three exit paths:
 *
 *   (A) Normal phase (deploy / attack / fortify, free sub-phase).
 *       Increment consecutive count, emit turnSkipped, transition
 *       turn to the next non-eliminated player.
 *
 *   (B) Mid-`awaitingAdvance`. Auto-advance with maxUnits first (the
 *       conquest already happened — no reason to undo). Emit the
 *       `advanced` event for the conquest move, then proceed as (A).
 *
 *   (C) Third consecutive skip. Emit turnSkipped + turnForfeited,
 *       delegate to applyForfeit which redistributes territories,
 *       emits playerEliminated, and may emit gameWon. The counter
 *       reset doesn't matter — the player is eliminated.
 *
 * The increment writes through state.consecutiveAutoSkips[playerId];
 * the dispatcher-level reset in applyAction handles the "any non-skip
 * action resets the counter" rule on the OTHER reducers.
 */
function applyAutoSkip(
  state: GameState,
  action: Extract<Action, { type: 'autoSkip' }>,
  map: MapDefinition
): ApplyResult {
  const events: GameEvent[] = [];

  // Path B prelude: if we're mid-awaitingAdvance, execute the implicit
  // advance with maxUnits BEFORE running the skip + transition. The
  // conquest already happened on the prior attack — the only thing
  // pending is moving units in.
  let working: GameState = state;
  if (
    working.turn.phase === 'attack' &&
    working.turn.attack.kind === 'awaitingAdvance'
  ) {
    const adv = working.turn.attack;
    const from = working.territories[adv.from]!;
    const to = working.territories[adv.to]!;
    const territories: Record<TerritoryId, TerritoryState> = {
      ...working.territories,
      [adv.from]: { ...from, units: from.units - adv.maxUnits },
      [adv.to]: { ...to, units: to.units + adv.maxUnits },
    };
    const turn: TurnContext = {
      ...working.turn,
      attack: { kind: 'free' },
    };
    working = { ...working, territories, turn };
    events.push({
      kind: 'advanced',
      from: adv.from,
      to: adv.to,
      units: adv.maxUnits,
    });
  }

  // Increment the consecutive-skip counter.
  const previousCount =
    (working.consecutiveAutoSkips ?? {})[action.playerId] ?? 0;
  const newCount = previousCount + 1;
  const consecutiveAutoSkips = {
    ...(working.consecutiveAutoSkips ?? {}),
    [action.playerId]: newCount,
  };

  events.push({
    kind: 'turnSkipped',
    playerId: action.playerId,
    turnNumber: working.turnNumber,
    consecutiveCount: newCount,
  });

  // Path C: third consecutive skip → forfeit.
  if (newCount >= 3) {
    events.push({
      kind: 'turnForfeited',
      playerId: action.playerId,
      consecutiveCount: newCount,
    });
    // Stamp the counter increment BEFORE forfeit so the forfeit
    // events reflect the final state shape.
    working = { ...working, consecutiveAutoSkips };
    const forfeitResult = applyForfeit(working, map, action.playerId);
    return {
      state: forfeitResult.state,
      events: [...events, ...forfeitResult.events],
    };
  }

  // Path A: normal skip — transition the turn to the next player.
  // Mirrors applyEndTurn but starting from `working` (which may have
  // already been advanced in Path B's prelude).
  const next = findNextPlayer(working, working.turn.currentPlayer);
  const turnNumber = working.turnNumber + next.turnIncrement;
  const phase: TurnPhase = turnNumber === 1 ? 'attack' : 'deploy';
  const reinforcements =
    phase === 'deploy' ? computeReinforcements(working, map, next.id) : 0;
  const turn: TurnContext = {
    currentPlayer: next.id,
    phase,
    attack: { kind: 'free' },
    reinforcementsRemaining: reinforcements,
    fortifyUsed: false,
    deploysThisTurn: [],
  };
  events.push({ kind: 'turnEnded', nextPlayer: next.id, turnNumber });

  return {
    state: {
      ...working,
      consecutiveAutoSkips,
      turn,
      turnNumber,
    },
    events,
  };
}

// --- Helpers ---

function checkEliminationsAndVictory(state: GameState): {
  state: GameState;
  events: readonly GameEvent[];
} {
  const events: GameEvent[] = [];
  const newlyEliminated: PlayerId[] = [];
  const newPlayers: Player[] = state.players.map((p) => {
    if (p.eliminated) return p;
    const owned = countOwned(state.territories, p.id);
    if (owned === 0) {
      events.push({ kind: 'playerEliminated', playerId: p.id });
      newlyEliminated.push(p.id);
      return { ...p, eliminated: true };
    }
    return p;
  });

  // Append in player-array iteration order. In practice only one player
  // can be eliminated per action (a single attack flips one territory),
  // but the reducer is defensive in case future rules trigger multi-kills.
  const eliminationOrder =
    newlyEliminated.length === 0
      ? state.eliminationOrder
      : [...state.eliminationOrder, ...newlyEliminated];

  const alive = newPlayers.filter((p) => !p.eliminated);
  let status = state.status;
  // Team-aware victory: the game ends when only one TEAM remains,
  // not necessarily one player. Solo FFA wins are the special case
  // where the surviving team has exactly one player. Multiple living
  // players sharing a team = joint victory.
  const survivingTeams = new Set(alive.map((p) => p.team));
  if (
    alive.length >= 1 &&
    survivingTeams.size === 1 &&
    state.status.kind === 'inProgress'
  ) {
    const winningTeam = alive[0]!.team;
    // Pick a representative winner — the player on the winning team
    // who appears first in `players[]`. Deterministic and stable
    // for replay / podium rendering.
    const representative = newPlayers.find(
      (p) => !p.eliminated && p.team === winningTeam
    )!;
    status = {
      kind: 'victory',
      winner: representative.id,
      winningTeam,
    };
    events.push({ kind: 'gameWon', winner: representative.id });
  }

  return {
    state: { ...state, players: newPlayers, status, eliminationOrder },
    events,
  };
}

function findNextPlayer(
  state: GameState,
  currentId: PlayerId
): { id: PlayerId; turnIncrement: number } {
  const order = state.playerOrder;
  const currentIdx = order.indexOf(currentId);
  if (currentIdx < 0) {
    throw new Error('current player not in playerOrder');
  }
  for (let step = 1; step <= order.length; step++) {
    const newIdx = (currentIdx + step) % order.length;
    const candidate = order[newIdx]!;
    const player = state.players.find((p) => p.id === candidate);
    if (!player) continue;
    if (!player.eliminated) {
      const wrapped = newIdx <= currentIdx;
      return { id: candidate, turnIncrement: wrapped ? 1 : 0 };
    }
  }
  throw new Error('findNextPlayer: no living players');
}
